import { useState } from 'react';
import { Box, Typography, Alert, Grid, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { usePricing } from '../../hooks/usePricing';
import PlanCard from '../PlanCard';
import { FREE_FEATURES, PREMIUM_FEATURES, getFormattedFeatures } from '../../config/pricingFeatures';

function SubscriptionStep({ formData, updateFormData, onNext }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const selectedInterval = 'month'; // Locked to monthly only
  const { pricing, loading: pricingLoading } = usePricing();

  const handleFreeSelect = () => {
    updateFormData({ subscriptionChoice: 'free' });
    onNext();
  };

  const handlePremiumSelect = async () => {
    setLoading(true);
    setError(null);

    try {
      // Store current form data in session storage for when user returns
      sessionStorage.setItem('onboardingData', JSON.stringify({
        ...formData,
        subscriptionChoice: 'premium',
      }));

      // Create Stripe checkout session
      const response = await api.post('/subscription/create-checkout-session', {
        returnPath: window.location.pathname,
        interval: selectedInterval,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError(err.response?.data?.error || t('onboarding.subscription.checkoutError'));
      setLoading(false);
    }
  };

  const freeFeatures = getFormattedFeatures(t, FREE_FEATURES);
  const premiumFeatures = getFormattedFeatures(t, PREMIUM_FEATURES);

  // Get premium price (monthly only)
  const getPremiumPrice = () => {
    if (pricingLoading) {
      return <Skeleton width="120px" />;
    }

    // Fallback price for monthly
    const getFallbackPrice = () => {
      return `39 SEK${t('pricing.billing.intervals.month')}`;
    };

    const currentPrice = pricing?.prices?.monthly;

    if (currentPrice) {
      return `${currentPrice.formatted.amount} ${currentPrice.formatted.currency}${t(`pricing.billing.intervals.${currentPrice.interval}`)}`;
    }

    // Translated fallback
    return getFallbackPrice();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('onboarding.subscription.heading')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('onboarding.subscription.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Free Plan Card */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <PlanCard
            variant="free"
            name={t('onboarding.subscription.freeCard.title')}
            price={t('onboarding.subscription.freeCard.price')}
            subtitle={t('onboarding.subscription.freeCard.description')}
            features={freeFeatures}
            buttonText={t('onboarding.subscription.freeCard.button')}
            onButtonClick={handleFreeSelect}
            onCardClick={handleFreeSelect}
            selected={formData.subscriptionChoice === 'free'}
            buttonPosition="bottom"
            compactFeatures
          />
        </Grid>

        {/* Premium Plan Card */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <PlanCard
            variant="premium"
            name={t('onboarding.subscription.premiumCard.title')}
            price={getPremiumPrice()}
            subtitle={t('onboarding.subscription.premiumCard.description')}
            features={premiumFeatures}
            buttonText={t('onboarding.subscription.premiumCard.button')}
            onButtonClick={handlePremiumSelect}
            onCardClick={handlePremiumSelect}
            selected={formData.subscriptionChoice === 'premium'}
            loading={loading}
            badge={t('onboarding.subscription.premiumCard.recommended')}
            buttonPosition="bottom"
            compactFeatures
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default SubscriptionStep;
